import React from 'react';
import styled from 'styled-components';

import { FilestackImageWithTextData } from 'api/types/shared.types';
import { Box } from 'components/Box';
import { TrackedConditionalLink } from 'components/Link';
import { BodySmall, H3 } from 'components/Text';
import { breakpoints, colors, gutterWidth } from 'theme';

import { ThreeStepCtaBlockStepBox } from './ThreeStepCtaBlockStepBox';
import { TreeStepCtaBlockStepImage } from './TreeStepCtaBlockStepImage';

type Props = {
  blockIndex: number;
  className?: string;
  headline: string;
  image?: FilestackImageWithTextData;
  text: string;
  url: string;
  withImages: boolean;
  withLinks: boolean;
  large?: boolean;
  pageUpdated: string;
  step: string;
  'data-qa-id': string;
};

export const TreeStepCtaBlockStep = styled(
  ({
    blockIndex,
    className,
    headline,
    image,
    text,
    url,
    withImages,
    withLinks,
    large,
    pageUpdated,
    step,
    ...props
  }: Props) => (
    <Box
      data-qa-id={props['data-qa-id']}
      maxWidth="400px"
      display="flex"
      alignItems="center"
      className={className}
      justifyContent="flex-start"
      flexDirection="column"
      flex="0 0 auto"
      width={[1, null, null, 1 / 3]}
      px={[0, null, null, gutterWidth / 2]}
      py={[gutterWidth / 2, null, null, 0]}
    >
      <ThreeStepCtaBlockStepBox url={url} withLinks={withLinks} large={large}>
        {(image || withImages) && (
          <Box
            position="relative"
            overflow="hidden"
            mb={24}
            width="100%"
            height={large ? 0 : 80}
            pb={large ? `${200 / 3}%` : 0}
          >
            {image && (
              <Box
                width="100%"
                height="100%"
                position="absolute"
                left="50%"
                transform="translateX(-50%)"
                textAlign="center"
              >
                <TrackedConditionalLink
                  eventName="CMS Block"
                  eventProperties={{
                    block_position: blockIndex,
                    block_type: 'Three Step Call to Action',
                    cta_type: 'Tile',
                    page_updated: pageUpdated,
                    step,
                  }}
                  link={url}
                  tabIndex={-1}
                >
                  <TreeStepCtaBlockStepImage
                    data-qa-id={`cta-step-image${large ? '-large' : ''}`}
                    alt={image.altText}
                    data={image}
                    height={large ? 240 : 80}
                    width={352}
                    fit="max"
                    format={large ? 'jpg' : undefined}
                    large={large}
                  />
                </TrackedConditionalLink>
              </Box>
            )}
          </Box>
        )}
        <Box width="100%" textAlign={large ? 'left' : 'center'}>
          <H3 mb="8px" styleAs={large ? 'h4' : 'h3'}>
            <TrackedConditionalLink
              eventName="CMS Block"
              eventProperties={{
                block_position: blockIndex,
                block_type: 'Three Step Call to Action',
                cta_type: 'Tile',
                page_updated: pageUpdated,
                step,
              }}
              link={url}
            >
              {headline}
            </TrackedConditionalLink>
          </H3>
          <BodySmall
            analyticsData={{
              eventName: 'CMS Block',
              eventProperties: {
                block_position: blockIndex,
                block_type: 'Three Step CTA',
              },
            }}
            px={[0, null, large ? null : gutterWidth / 4]}
            mb={-16}
            dangerouslySetInnerHTML={{
              __html: text,
            }}
          />
        </Box>
      </ThreeStepCtaBlockStepBox>
    </Box>
  )
)`
  ${(props) =>
    !props.large &&
    !props.withLinks &&
    `
    border-bottom: solid 1px ${colors.silver};

    &:last-child {
      border-bottom: 0;
    }

    @media (min-width: ${breakpoints[2]}) {
      border-bottom: none;
      border-right: solid 1px ${colors.silver};

      &:last-child {
        border-right: 0;
      }
    }
  `}
`;
